/**
 * Phase 2A-ii — Custom Material Dialog
 *
 * Studio workflow:
 *   1. Fill in the basic identity fields (Name, Category, Manufacturer,
 *      Supplier, Finish, Source).
 *   2. Optionally upload a reference image (supplier photo, client
 *      sample, architect sketch) — the file is pushed through the
 *      existing `/api/upload` endpoint which persists to Emergent Object
 *      Storage (NOT base64 in Mongo).
 *   3. On Save:
 *        a. POST /api/materials/custom            → creates the Product
 *        b. POST /api/materials/{id}/visual-assets → creates V1 with
 *           `asset_paths = { thumbnail, albedo }` when an image was
 *           supplied. The backend `readiness_from_asset_paths` classifier
 *           then labels the asset as `ai_reference_ready` (thumbnail +
 *           albedo but no normal / roughness). Studio never invents a
 *           `3D Texture Ready` or `Full PBR Ready` label — the backend
 *           is authoritative.
 *
 * The created product is `is_custom: true` and flows through the normal
 * MaterialPicker (Used / Favourites / Recent / Compatible / Search All)
 * so it can be assigned to any Material Slot.
 */
import { useState } from "react";
import { X, Upload, Loader2 } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_HINTS = [
  "laminate", "veneer", "decorative_board", "melamine", "painted",
  "stone", "porcelain", "compact_laminate", "timber",
  "cabinet_board", "moisture_board",
  "acoustic", "microcement", "venetian", "tile", "metal", "fabric",
  "glass", "mirror", "smoked_mirror",
];

export default function CustomMaterialDialog({ open, allowedCategories, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState((allowedCategories && allowedCategories[0]) || "laminate");
  const [manufacturer, setManufacturer] = useState("");
  const [finish, setFinish] = useState("");
  const [source, setSource] = useState("client_reference");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!open) return null;
  const reset = () => {
    setName(""); setManufacturer(""); setFinish(""); setNotes(""); setFile(null);
    setError(""); setBusy(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError("Name is required"); return; }
    setError(""); setBusy(true);
    try {
      // Step 1 — upload the reference image to Emergent Object Storage.
      let storagePath = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "custom_material");
        const { data: up } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        storagePath = up.storage_path;
      }
      // Step 2 — create the Custom Material (Product).
      const { data: prod } = await api.post("/materials/custom", {
        name: name.trim(),
        category,
        manufacturer: manufacturer.trim() || null,
        finish: finish.trim() || null,
        custom_source: {
          source_type: source,
          notes: notes.trim() || null,
          reference_storage_path: storagePath,
        },
        notes: notes.trim() || null,
      });
      // Attach the uploaded reference to the product row so the picker
      // fallback thumbnail also works when the visual asset chain is
      // not consulted (e.g., in legacy contexts).
      if (storagePath) {
        try {
          await api.put(`/products/${prod.id}`, { image_path: storagePath });
        } catch (_) { /* non-fatal: picker still resolves via visual asset */ }
      }
      // Step 3 — create the initial visual asset. When we have an image
      // we register it as BOTH thumbnail and albedo so the backend
      // classifier lands on `ai_reference_ready` (per Phase 2A-i spec).
      // Without an image we still create V1 as `thumbnail_only` so the
      // Design Option pinning chain has a valid asset row to reference.
      const assetPaths = storagePath
        ? { thumbnail: storagePath, albedo: storagePath }
        : {};
      const { data: va } = await api.post(`/materials/${prod.id}/visual-assets`, {
        material_id: prod.id,
        asset_paths: assetPaths,
        source_metadata: {
          source_type: source,
          uploaded_by_studio: true,
        },
      });
      toast.success(`Custom Material “${prod.name}” created (${va.readiness_level.replaceAll("_", " ")})`);
      reset();
      onCreated && onCreated({ ...prod, visual_asset: {
        id: va.id, version: va.version,
        readiness_level: va.readiness_level,
        thumbnail: storagePath,
      }});
    } catch (err) {
      setError(formatErr(err));
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3"
         data-testid="custom-material-dialog"
         onClick={onClose}>
      <div className="bg-background border border-border max-w-md w-full p-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium">New Custom Material</div>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="w-7 h-7 inline-flex items-center justify-center border border-border hover:border-foreground">
            <X className="w-3.5 h-3.5" strokeWidth={1.5}/>
          </button>
        </div>
        <form onSubmit={submit} className="space-y-2">
          <div>
            <label className="overline text-[10px] block mb-1">Name</label>
            <input data-testid="cm-name" className="luxe-input w-full"
                   placeholder="e.g. European Smoked Oak Natural Grain"
                   value={name} onChange={(e) => setName(e.target.value)} required/>
          </div>
          <div>
            <label className="overline text-[10px] block mb-1">Category</label>
            <select data-testid="cm-category" className="luxe-input w-full"
                    value={category} onChange={(e) => setCategory(e.target.value)}>
              {(allowedCategories && allowedCategories.length ? allowedCategories : CATEGORY_HINTS)
                .map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="overline text-[10px] block mb-1">Manufacturer</label>
              <input data-testid="cm-manufacturer" className="luxe-input w-full"
                     value={manufacturer} onChange={(e) => setManufacturer(e.target.value)}/>
            </div>
            <div>
              <label className="overline text-[10px] block mb-1">Finish</label>
              <input data-testid="cm-finish" className="luxe-input w-full"
                     value={finish} onChange={(e) => setFinish(e.target.value)}/>
            </div>
          </div>
          <div>
            <label className="overline text-[10px] block mb-1">Source</label>
            <select data-testid="cm-source" className="luxe-input w-full"
                    value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="client_reference">Client Reference</option>
              <option value="architect_reference">Architect Reference</option>
              <option value="interior_designer_reference">Interior Designer Reference</option>
              <option value="supplier_sample">Supplier Sample</option>
              <option value="builder_reference">Builder Reference</option>
              <option value="site_photo">Site Photo</option>
            </select>
          </div>
          <div>
            <label className="overline text-[10px] block mb-1">Reference image (optional)</label>
            <label data-testid="cm-file-label"
                   className="luxe-input w-full flex items-center gap-2 cursor-pointer">
              <Upload className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5}/>
              <span className="truncate text-[11px]">
                {file ? file.name : "Choose an image (JPG or PNG, up to 12 MB)"}
              </span>
              <input data-testid="cm-file" type="file" accept="image/*"
                     className="hidden"
                     onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}/>
            </label>
            <div className="text-[9px] text-muted-foreground mt-1">
              A reference photo is classified as <strong>AI Reference Ready</strong>. It is not
              a seamless 3D texture — Studio can supersede this asset later with proper PBR maps.
            </div>
          </div>
          <div>
            <label className="overline text-[10px] block mb-1">Notes (optional)</label>
            <input data-testid="cm-notes" className="luxe-input w-full"
                   placeholder="e.g. Match to sample #A-27 supplied by client"
                   value={notes} onChange={(e) => setNotes(e.target.value)}/>
          </div>
          {error && (
            <div className="text-[11px] text-destructive" data-testid="cm-error">{error}</div>
          )}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
                    className="flex-1 h-9 border border-border hover:border-foreground text-[11px]">
              Cancel
            </button>
            <button type="submit" disabled={busy}
                    data-testid="cm-submit"
                    className="flex-1 h-9 border border-foreground bg-foreground text-background text-[11px] disabled:opacity-50 inline-flex items-center justify-center gap-1">
              {busy && <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5}/>}
              {busy ? "Creating…" : "Create Material"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
